import React, { useEffect } from "react";
import SecureLogo from "../../../assets/img/RJ_V1/100secure.png";
import ChronicLogo from "../../../assets/img/RJ_V1/chronic.png";

const BasicPersonalDetails = ({
  slideView,
  register,
  validationMsg,
  clearErrors,
  watch,
  setIsNextActive,
}) => {
  const title = watch("lstSalutation");
  const firstName = watch("txtFName");
  const lastName = watch("txtLName");

  // Update sticky button state whenever watched values change
  useEffect(() => {
    const isActive =
      title?.trim() &&
      firstName?.trim()?.length >= 3 &&
      lastName?.trim()?.length >= 3;
    setIsNextActive(!!isActive);
  }, [title, firstName, lastName, setIsNextActive]);

  // Reset button when slide becomes active
  useEffect(() => {
    if (slideView === "show") {
      const isActive =
        title?.trim() &&
        firstName?.trim()?.length >= 3 &&
        lastName?.trim()?.length >= 3;
      setIsNextActive(!!isActive);
    }
  }, [slideView]);

  return (
    <div
      className={`slide ${slideView === "show" ? "active" : "d-none"}`}
      id="slide-1"
    >
      <h2 className="fs-4 fw-bold tracking-tight text-gray-900 animate-stagger-up delay-200">
        Basic Personal Details
      </h2>
      <p className="fw-medium text-gray-700 mb-4 animate-stagger-up delay-200 fs-15">
        Tell us who we're helping
      </p>

      {/* Trust badges */}
      <div className="d-flex align-items-center gap-4 mb-5 border-top border-bottom border-gray-100 py-3 justify-content-center justify-content-md-start animate-stagger-up delay-400">
        <div className="d-flex align-items-center gap-2">
          <img src={SecureLogo} className="trust-badge-icon" alt="100% Secure" />
          <span className="small fw-bold text-gray-900">100% Secure</span>
        </div>
        <div
          style={{
            height: "2rem",
            width: "1px",
            flexShrink: 0,
            backgroundColor: "#d1d5db",
          }}
        ></div>
        <div className="d-flex align-items-center gap-2">
          <img
            src={ChronicLogo}
            className="trust-badge-icon"
            alt="Takes less than 5 minutes"
          />
          <span className="small fw-bold text-gray-900 leading-tight">
            Takes less than
            <br />
            <span className="text-brand-green">5 minutes</span>
          </span>
        </div>
      </div>

      <div className="mx-auto animate-stagger-up delay-600">
        {/* Title */}
        <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-75 md-w-50 mb-4 transition-colors rounded-10 ">
          <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">
            Title <span className="text-red-500">*</span>
          </legend>
          <div className="position-relative">
            <select
              name="lstSalutation"
              ref={register({ required: "Please select a title" })}
              className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2 cursor-pointer"
              style={{ outline: "none" }}
              onChange={() => clearErrors("lstSalutation")}
            >
              <option value="">Please select</option>
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Ms">Ms</option>
              <option value="Dr">Dr</option>
              <option value="Prof">Prof</option>  
            </select>
            <svg
              className="position-absolute pointer-events-none text-blue-900 icon-sm"
              style={{
                right: "0.5rem",
                top: "50%",
                transform: "translateY(-50%)",
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="3"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </fieldset>
        {validationMsg?.lstSalutation && (
          <p className="error_msg">{validationMsg.lstSalutation.message}</p>
        )}

        {/* First Name */}
        <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 mb-4 transition-colors rounded-10">
          <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">
            First Name <span className="text-red-500">*</span>
          </legend>
          <input
            type="text"
            name="txtFName"
            ref={register({
              required: "Please Enter First Name",
              minLength: {
                value: 3,
                message: "Please Enter Valid First Name",
              },
              pattern: {
                value: /^[a-zA-Z\s]+$/,
                message: "Your Name is Invalid",
              },
            })}
            className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2"
            style={{ outline: "none" }}
            placeholder="First Name"
            onChange={() => clearErrors("txtFName")}
          />
        </fieldset>
        {validationMsg?.txtFName && (
          <p className="error_msg">{validationMsg.txtFName.message}</p>
        )}

        {/* Last Name */}
        <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 mb-4 transition-colors rounded-10">
          <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">
            Last Name <span className="text-red-500">*</span>
          </legend>
          <input
            type="text"
            name="txtLName"
            ref={register({
              required: "Please Enter Last Name",
              minLength: {
                value: 3,
                message: "Please Enter Valid Last Name",
              },
              maxLength: {
                value: 20,
                message: "Last Name should not exceed 20 characters",
              },
              pattern: {
                value: /^[a-zA-Z\s]+$/,
                message: "Your Name is Invalid",
              },
            })}
            className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2"
            style={{ outline: "none" }}
            placeholder="Last Name"
            onChange={() => clearErrors("txtLName")}
          />
        </fieldset>
        {validationMsg?.txtLName && (
          <p className="error_msg">{validationMsg.txtLName.message}</p>
        )}

        {/* Info banner */}
        <div className="d-flex align-items-center gap-4 bg-blue-50-half rounded-4 p-3 mt-5 border border-blue-100 shadow-sm animate-pop-in">
          <div className="flex-shrink-0 bg-white rounded-3 shadow-sm d-flex align-items-center justify-content-center p-2 w-12 h-12">
            <svg viewBox="0 0 100 100" className="w-100 h-100">
              <circle cx="50" cy="35" r="15" fill="#cfd8dc" />
              <rect x="30" y="55" width="40" height="30" rx="10" fill="#2196f3" />
              <circle cx="20" cy="50" r="10" fill="#4caf50" />
              <path
                d="M15 50 L18 53 L25 45"
                stroke="white"
                strokeWidth="3"
                fill="none"
              />
            </svg>
          </div>
          <p className="fw-semibold text-gray-600 leading-tight mb-0 fs-14">
            This will be used to check if you’ve had a finance agreement with one of the lenders
          </p>
        </div>
      </div>
    </div>
  );
};

export default BasicPersonalDetails;